import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const STAFF_ROLES = new Set(["admin", "shop_manager"]);

// Client-side password strength scoring for the Register form.
// Score 0-4 based on: length ≥ 8, has upper+lower, has digit, has special char.
const STRENGTH_LABELS = ["Too short", "Weak", "Okay", "Good", "Strong"];
const STRENGTH_COLORS = [
    "bg-gray-200",
    "bg-rose-500",
    "bg-amber-500",
    "bg-yellow-500",
    "bg-emerald-500",
];
function scorePassword(pwd) {
    if (!pwd) return -1;
    if (pwd.length < 6) return 0;
    let s = 0;
    if (pwd.length >= 8) s += 1;
    if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) s += 1;
    if (/\d/.test(pwd)) s += 1;
    if (/[^A-Za-z0-9]/.test(pwd)) s += 1;
    return Math.max(1, s);
}

export default function AuthDialog({ open, onOpenChange }) {
    const { login, register } = useAuth();
    const navigate = useNavigate();
    const [mode, setMode] = useState("login"); // login | register | forgot
    const [form, setForm] = useState({ name: "", email: "", password: "" });
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    const reset = () => {
        setError("");
        setForm({ name: "", email: "", password: "" });
    };

    const goToDashboard = (role) => {
        navigate(STAFF_ROLES.has(role) ? "/admin" : "/account");
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            if (mode === "login") {
                const res = await login(form.email, form.password);
                if (res.ok) {
                    toast.success("Welcome back!");
                    onOpenChange(false);
                    reset();
                    goToDashboard(res.user?.role);
                } else setError(res.error);
            } else if (mode === "register") {
                const res = await register(form.name, form.email, form.password);
                if (res.ok) {
                    toast.success("Welcome to Aham Yog");
                    onOpenChange(false);
                    reset();
                    goToDashboard(res.user?.role);
                } else setError(res.error);
            } else if (mode === "forgot") {
                await api.post("/auth/forgot-password", { email: form.email });
                toast.success("Check your inbox", {
                    description: "If an account exists, a reset link was sent.",
                });
                setMode("login");
                reset();
            }
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md rounded-3xl" data-testid="auth-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">
                        {mode === "login" && "Welcome back"}
                        {mode === "register" && "Create your account"}
                        {mode === "forgot" && "Reset your password"}
                    </DialogTitle>
                    <DialogDescription>
                        {mode === "login" && "Sign in to continue your practice."}
                        {mode === "register" && "Join the Aham Yog community."}
                        {mode === "forgot" && "Enter your email — we'll send a reset link."}
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={onSubmit} className="space-y-4">
                    {mode === "register" && (
                        <div>
                            <Label htmlFor="auth-name">Name</Label>
                            <Input
                                id="auth-name"
                                name="name"
                                value={form.name}
                                onChange={onChange}
                                required
                                className="mt-1.5 rounded-xl"
                                data-testid="auth-name-input"
                            />
                        </div>
                    )}
                    <div>
                        <Label htmlFor="auth-email">Email</Label>
                        <Input
                            id="auth-email"
                            type="email"
                            name="email"
                            value={form.email}
                            onChange={onChange}
                            required
                            className="mt-1.5 rounded-xl"
                            data-testid="auth-email-input"
                        />
                    </div>
                    {mode !== "forgot" && (
                        <div>
                            <div className="flex items-center justify-between">
                                <Label htmlFor="auth-password">Password</Label>
                                {mode === "login" && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setMode("forgot");
                                            setError("");
                                        }}
                                        className="text-xs font-medium text-brand-600 hover:text-brand-700"
                                        data-testid="auth-forgot-link"
                                    >
                                        Forgot password?
                                    </button>
                                )}
                            </div>
                            <div className="relative mt-1.5">
                                <Input
                                    id="auth-password"
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    value={form.password}
                                    onChange={onChange}
                                    required
                                    minLength={6}
                                    className="rounded-xl pr-10"
                                    data-testid="auth-password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword((v) => !v)}
                                    className="absolute inset-y-0 right-0 flex items-center px-3 text-ink-700 hover:text-brand-600 focus:outline-none"
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                    aria-pressed={showPassword}
                                    data-testid="auth-password-toggle"
                                    tabIndex={-1}
                                >
                                    {showPassword ? <EyeOff className="h-4 w-4" strokeWidth={1.8} /> : <Eye className="h-4 w-4" strokeWidth={1.8} />}
                                </button>
                            </div>
                            {mode === "register" && form.password.length > 0 && (() => {
                                const score = scorePassword(form.password);
                                const idx = Math.max(0, score);
                                return (
                                    <div
                                        className="mt-2"
                                        data-testid="auth-password-strength"
                                        data-strength={STRENGTH_LABELS[idx].toLowerCase().replace(" ", "-")}
                                    >
                                        <div className="flex gap-1.5" aria-hidden="true">
                                            {[1, 2, 3, 4].map((seg) => (
                                                <span
                                                    key={seg}
                                                    className={`h-1.5 flex-1 rounded-full transition-colors ${seg <= score ? STRENGTH_COLORS[idx] : "bg-gray-200"}`}
                                                />
                                            ))}
                                        </div>
                                        <p className="mt-1.5 text-[11px] text-ink-700">
                                            Password strength:{" "}
                                            <span
                                                className={`font-semibold ${
                                                    idx >= 4 ? "text-emerald-700"
                                                    : idx >= 3 ? "text-yellow-700"
                                                    : idx >= 2 ? "text-amber-700"
                                                    : "text-rose-700"
                                                }`}
                                                data-testid="auth-password-strength-label"
                                            >
                                                {STRENGTH_LABELS[idx]}
                                            </span>
                                        </p>
                                    </div>
                                );
                            })()}
                        </div>
                    )}

                    {error && (
                        <div
                            className="text-sm text-accent-700 bg-accent-50 px-3 py-2 rounded-lg"
                            data-testid="auth-error"
                        >
                            {error}
                        </div>
                    )}

                    <Button
                        type="submit"
                        disabled={loading}
                        className="w-full rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta py-5 text-base"
                        data-testid="auth-submit-btn"
                    >
                        {loading
                            ? "Please wait..."
                            : mode === "login"
                            ? "Sign in"
                            : mode === "register"
                            ? "Create account"
                            : "Send reset link"}
                    </Button>

                    <div className="text-center text-sm text-ink-700">
                        {mode === "forgot" ? (
                            <button
                                type="button"
                                onClick={() => {
                                    setMode("login");
                                    setError("");
                                }}
                                className="font-semibold text-brand-600 hover:text-brand-700"
                                data-testid="auth-back-to-login"
                            >
                                Back to sign in
                            </button>
                        ) : (
                            <>
                                {mode === "login" ? "New here?" : "Already have an account?"}{" "}
                                <button
                                    type="button"
                                    onClick={() => {
                                        setMode(mode === "login" ? "register" : "login");
                                        setError("");
                                    }}
                                    className="font-semibold text-brand-600 hover:text-brand-700"
                                    data-testid="auth-toggle-mode"
                                >
                                    {mode === "login" ? "Create account" : "Sign in"}
                                </button>
                            </>
                        )}
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
